import { Vector3 } from 'three';

import { GeodataStoreFactory } from '../../stores/geodata-store-factory/geodata-store-factory';
import { Cesium3dTiles } from '../geodata/3d-tiled-models/cesium-3d-tiles/cesium-3d-tiles';
import { Cesium3dTilesRenderer } from '../geodata/3d-tiled-models/cesium-3d-tiles/cesium-3d-tiles-renderer';
import { GltfTree } from '../geodata/3d-tiled-models/gltf-tree/gltf-tree';
import { Tiled3dModel } from '../geodata/3d-tiled-models/type';
import { Cesium3dTilesPointCloudRenderer } from '../geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-point-cloud-renderer';
import { CesiumPointCloud } from '../geodata/point-clouds/cesium-point-cloud/cesium-point-cloud';
import { createPointsFactory } from '../geodata/point-clouds/cesium-point-cloud/points/factory';
import { CopcPointCloud } from '../geodata/point-clouds/copc-point-cloud/copc-point-cloud';
import { PointCloud } from '../geodata/point-clouds/types';
import { CesiumTileSetHierarchy } from '../loader/cesium/tile-set';
import { PointCloudManager } from '../loader/pointcloud/point-cloud-manager';
import { ProjectGeodataManager } from '../project-geodata-manager';

/**
 * Activates the point cloud or tiled model, clearing it and throwing the signal's AbortError if
 * the signal is aborted before it's done, so nothing is left loading for a timeline no longer
 * shown.
 */
async function activateUnlessAborted<T extends { clearCustom(): void }>(
  geodata: T,
  activate: () => Promise<void> | void,
  signal?: AbortSignal,
): Promise<T> {
  try {
    await activate();
    signal?.throwIfAborted();
  } catch (err) {
    geodata.clearCustom();
    throw err;
  }
  return geodata;
}

/**
 * Loads the active timeline container's tiled model, or its legacy scan, not yet activated.
 * Throws an AbortError once the signal is aborted.
 */
export async function loadTiledModel(signal?: AbortSignal): Promise<Tiled3dModel | undefined> {
  if (ProjectGeodataManager.instance().hasTiledModelData()) {
    const tiledModelData = ProjectGeodataManager.instance().getActiveTiledModelData()!;

    /**
     * Legacy tiled model is not standard, for example the b3dm data doesnt have
     * magic bytes and the tileset files have additional properties that makes
     * it incompatible with the 3d-tiles-renderer.
     * So for these files we use the old tiles renderer.
     */
    const isLegacyTiledModel = tiledModelData.version === '211001';

    if (isLegacyTiledModel) {
      const cesiumTiledModelStore = await GeodataStoreFactory.instance.createTiledModelStore(
        tiledModelData.id,
      );
      signal?.throwIfAborted();

      const cesiumTileSetHierarchy = new CesiumTileSetHierarchy(cesiumTiledModelStore);

      await cesiumTileSetHierarchy.initialize();
      signal?.throwIfAborted();

      return new Cesium3dTiles(cesiumTileSetHierarchy, new Vector3(...tiledModelData.origo));
    }

    const signer = await GeodataStoreFactory.instance.createTiledModelCloudFrontSigner(
      tiledModelData.id,
    );
    signal?.throwIfAborted();
    return new Cesium3dTilesRenderer(signer, new Vector3(...tiledModelData.origo));
  }

  if (ProjectGeodataManager.instance().hasScanData()) {
    const gltfTree = new GltfTree();
    return activateUnlessAborted(gltfTree, () => gltfTree.initialize(), signal);
  }

  return undefined;
}

/**
 * Loads the active timeline container's point cloud, or none. Throws an AbortError once the
 * signal is aborted, having stopped what it started, such as a COPC point cloud's workers.
 */
export async function loadPointCloud(signal?: AbortSignal): Promise<PointCloud | undefined> {
  if (ProjectGeodataManager.instance().hasCesiumPointCloudData()) {
    const cesiumPointCloudData =
      ProjectGeodataManager.instance().getActiveCesiumPointCloudGeodata()!;

    if (cesiumPointCloudData.format === 'COPC') {
      const url = await GeodataStoreFactory.instance.createCopcPointCloudUrl(
        cesiumPointCloudData.id,
      );
      signal?.throwIfAborted();
      const copcPointCloud = new CopcPointCloud(url, new Vector3(...cesiumPointCloudData.origo));
      // Stops its workers on failure too, which nothing else would reach
      return activateUnlessAborted(copcPointCloud, () => copcPointCloud.activate(), signal);
    }

    if (cesiumPointCloudData.refine === 'REPLACE') {
      const cesiumPointCloudStore = await GeodataStoreFactory.instance.createCesiumPointCloudStore(
        cesiumPointCloudData.id,
      );
      signal?.throwIfAborted();

      const cesiumTileSetHierarchy = new CesiumTileSetHierarchy(cesiumPointCloudStore);
      await cesiumTileSetHierarchy.initialize();
      const pointsFactory = await createPointsFactory();
      signal?.throwIfAborted();

      const cesiumPointCloud = new CesiumPointCloud(
        cesiumTileSetHierarchy,
        new Vector3(...cesiumPointCloudData.origo),
        pointsFactory,
      );

      cesiumPointCloud.initialize();
      return cesiumPointCloud;
    }

    const signer = await GeodataStoreFactory.instance.createCesiumPointCloudCloudFrontSigner(
      cesiumPointCloudData.id,
    );
    signal?.throwIfAborted();
    const pointCloudRenderer = new Cesium3dTilesPointCloudRenderer(
      signer,
      new Vector3(...cesiumPointCloudData.origo),
    );
    return activateUnlessAborted(pointCloudRenderer, () => pointCloudRenderer.activate(), signal);
  }

  if (ProjectGeodataManager.instance().hasPointcloudData()) {
    PointCloudManager.instance.loadPointcloud();
    return PointCloudManager.instance;
  }

  return undefined;
}
